import { CopyOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Input,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Table,
  Tabs,
  message,
  notification,
} from "antd";
import dayjs from "dayjs";
import { throttle } from "lodash";
import { useContext, useEffect, useState } from "react";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { HeaderTitle } from "../../components/styled-components";
import { UserContext } from "../../context/userContext";
import ConfirmCancelSo from "../../pages/OrderSo/view/confirmCancelSo";
import { OrderSo } from "../../res/type";
import { GET, PATCH, POST } from "../../services";
import {
  CHECKER_CANCEL_SO,
  CHECKER_GET_ORDER_SO,
  CHECKER_OPEN_SO_RESULT,
  CHECKER_SEARCH_BY_ORDER_SO,
} from "../../services/path";
import "./style.css";

interface StatusOptionSO {
  value: string;
  label: string;
}

interface OpenSo {
  order_no: string;
  tracking_no: string;
  brand: string;
}

interface CancelSo {
  no: number;
  order_no: string;
  tracking_no: string;
  so_no: string;
  brand: string;
}

const { TabPane } = Tabs;
const SoManagement = () => {
  const [searchParams, setSearchParams] = useState({
    searchTextOrderNo: "",
    searchTextTracking: "",
    searchTextCategory: "",
    searchTextStatus: "",
    searchTextPlatform: "",
    searchTextLogistic: "",
    searchTextBrand: "",
    searchTextInvoice: "",
    searchTextSo: "",
    searchTextSoStatus: "",
  });

  const [inputData, setInputData] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [orders, setOrders] = useState<OrderSo[]>([]);
  const [allOrders, setAllOrder] = useState<OrderSo[]>([]);
  const [openSoOrder, setOpenSoOrder] = useState<OrderSo[]>([]);
  const [cancelSoOrder, setCancelSoOrder] = useState<OrderSo[]>([]);
  const [isReloading, setIsreloading] = useState(false);
  const hasLocalStorageDate = localStorage.getItem("date") !== null;
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    hasLocalStorageDate ? null : dayjs(),
  );

  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(hasLocalStorageDate ? null : dayjs());
  const [isloading, setLoading] = useState(false);
  const [isloadingOpen, setLoadingOpen] = useState(false);
  const [isloadingCancel, setLoadingCancel] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [activeTabManual, setActiveTabManual] = useState(false);
  const [selectedRowsOpen, setSelectedRowsOpen] = useState<OpenSo[]>([]);
  const [selectedRowsCancel, setSelectedRowsCancel] = useState<CancelSo[]>([]);
  const [confirmModalInfo, setConfirmModalInfo] = useState<{
    visible: boolean;
    trackingNo?: string;
    orderNo?: string;
  }>({ visible: false });

  const { user } = useContext(UserContext);

  // Define the status options with specific values and labels
  const statusOptionsSO: StatusOptionSO[] = [
    { value: "1", label: "Waiting Open" },
    { value: "2", label: "In Process" },
    { value: "3", label: "Opened" },
    { value: "4", label: "Canceled" },
    { value: "5", label: "UNKNOW" },
  ];

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleOpenConfirmModal = async (selectedRow: { tracking_no: string; order_no: string }) => {
    if (selectedRow) {
      const { tracking_no, order_no } = selectedRow;
      setConfirmModalInfo({ visible: true, trackingNo: tracking_no, orderNo: order_no });
    }
  };

  const handleCloseConfirmModal = async () => {
    setConfirmModalInfo({ visible: false, trackingNo: undefined, orderNo: undefined }); // Reset the modal state
    await fetchData();
  };

  const resetStatesConfirmModal = () => {
    setConfirmModalInfo({ visible: false, trackingNo: undefined, orderNo: undefined }); // Reset modal state
  };

  const handleMatch = async () => {
    handleCancelSo();
    await fetchData();
    resetStatesConfirmModal();
  };

  const handleReloadOrder = () => {
    try {
      setIsreloading(true);
      setOpenSoOrder([]);
      setCancelSoOrder([]);
      setAllOrder([]);
      fetchData2(startDate, endDate);
      localStorage.clear();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsreloading(false);
      setInputData("");
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setAllOrder([]);
      setOpenSoOrder([]);
      setCancelSoOrder([]);
      if (inputData) {
        const requestBody = { search_word: inputData };
        const result = await POST(CHECKER_SEARCH_BY_ORDER_SO, requestBody);
        if (activeTab === "3") {
          const cancelSoOrderNew = result.filter(
            (item: { so_status: string }) => item.so_status === "3" || item.so_status === "4",
          );

          setCancelSoOrder(cancelSoOrderNew || []);
        } else {
          setOrders(result);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setInputData("");
    }
  };

  const handleSearchInputChange = (field: string, value: string) => {
    setSearchParams({ ...searchParams, [field]: value });
    setCurrentPage(1);
  };

  const throttledFetchData = throttle(async () => {
    try {
      setLoading(true);
      const searchOrder = CHECKER_GET_ORDER_SO(startDate, endDate);
      const result = await GET(searchOrder);
      setOrders(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, 5000); // Adjust the throttle time (in milliseconds) as per your requirement

  const fetchData = async () => {
    throttledFetchData();
  };

  const fetchData2 = async (startDateTime: any, endDateTime: any) => {
    try {
      setLoading(true);
      const searchOrder = CHECKER_GET_ORDER_SO(startDateTime, endDateTime);
      const result = await GET(searchOrder);
      setOrders(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const searchInput = (
    field: string,
    phd?: string,
    valueLocal?: string | number | readonly string[] | undefined,
  ) => {
    return (
      <Input
        placeholder={phd}
        value={valueLocal}
        onChange={(e) => handleSearchInputChange(field, e.target.value)}
        onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
        style={{ width: 200 }}
        suffix={<SearchOutlined style={{ color: "gray" }} />}
      />
    );
  };

  const classifyBrand = (brand: string) => {
    const lowerCaseBrand = brand.toLowerCase();
    if (
      lowerCaseBrand === "bewell" ||
      lowerCaseBrand === "bewell-nonmal" ||
      lowerCaseBrand === "bestvalue"
    ) {
      return "bewell";
    } else {
      return "focus";
    }
  };

  const handleOpenSo = async () => {
    try {
      setLoadingOpen(true);
      if (selectedRowsOpen.length > 25) {
        notification.warning({
          message: "Warning",
          description: "You cannot open SO for more than 25 rows.",
          duration: 3, // Duration of the notification
        });
      } else if (selectedRowsOpen.length > 0) {
        for (const row of selectedRowsOpen) {
          const classifiedBrand = classifyBrand(row.brand);
          const requestBody = {
            packer_id: user.user_id,
            order_no: row.order_no,
            brand: classifiedBrand,
          };

          const result = await POST(CHECKER_OPEN_SO_RESULT, requestBody);
          const res = String(result["result"]);
          if (res.toLowerCase().includes("error")) {
            message.error("open error");
          } else {
            message.success("open success");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingOpen(false);
      setSelectedRowsOpen([]);
      fetchData();
    }
  };

  const handleCancelSo = async () => {
    try {
      setLoadingCancel(true);
      if (selectedRowsCancel.length > 0) {
        for (const row of selectedRowsCancel) {
          const requestBody = {
            userID: user.user_id,
            orderNo: row.order_no,
            soNo: row.so_no,
            no: row.no,
          };

          const result = await PATCH(CHECKER_CANCEL_SO, requestBody);
          if (result.success) {
            message.success("ยกเลิก Sale Order สำเร็จ");
          } else {
            message.error("ยกเลิก Sale Order ไม่สำเร็จ");
          }
        }
      }
    } catch (error) {
      message.error("ยกเลิก Sale Order ไม่สำเร็จ");
    } finally {
      setLoadingCancel(false);
      setSelectedRowsCancel([]);
      setCancelSoOrder([]);
    }
  };

  const handleTabClick = () => {
    setActiveTabManual(true);
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setSelectedRowsOpen([]);
    setSelectedRowsCancel([]);
    if (activeTabManual) {
      fetchData2(startDate, endDate);
      if (activeTab === "1") {
        const allSoOrder = orders.filter((item) => item.operation_status !== "cancel");
        const sortedAllOrders = allSoOrder.slice().sort((a, b) => a.no - b.no);
        setAllOrder(sortedAllOrders);
      } else if (activeTab === "2") {
        const openSoOrderNew = orders.filter(
          (item) => item.so_status === "1" && item.operation_status !== "cancel",
        );

        const sortedOpenSoOrders = openSoOrderNew.slice().sort((a, b) => a.no - b.no);
        setOpenSoOrder(sortedOpenSoOrders);
      } else {
        setCancelSoOrder([]);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    const filteredOrders = orders.filter((item) => {
      const checkMatch = (fieldValue: string, searchValue: string) => {
        if (!searchValue) return true; // No filter applied if searchValue is empty

        // Split the searchValue into an array if it contains multiple values
        const searchValues = searchValue.split(", ").map((val) => val.trim());
        return searchValues.some((val) => {
          if (val.includes("*")) {
            // Remove '*' and perform partial match
            return fieldValue.toLowerCase().includes(val.replace("*", "").toLowerCase());
          } else {
            // Perform exact match
            return fieldValue.trim().toLowerCase() === val.toLowerCase();
          }
        });
      };
      // Convert so_status numeric value to label
      const soStatusLabel =
        statusOptionsSO.find((option) => option.value === item.so_status)?.label || "";

      return (
        checkMatch(item.order_no ?? "", searchParams.searchTextOrderNo) &&
        checkMatch(item.tracking_no ?? "", searchParams.searchTextTracking) &&
        checkMatch(item.category ?? "", searchParams.searchTextCategory) &&
        checkMatch(item.platform ?? "", searchParams.searchTextPlatform) &&
        checkMatch(item.logistic ?? "", searchParams.searchTextLogistic) &&
        checkMatch(item.brand ?? "", searchParams.searchTextBrand) &&
        checkMatch(item.operation_status ?? "", searchParams.searchTextStatus) &&
        checkMatch(item.invoice_no ?? "", searchParams.searchTextInvoice) &&
        checkMatch(soStatusLabel ?? "", searchParams.searchTextSoStatus) &&
        checkMatch(item.so_no ?? "", searchParams.searchTextSo)
      );
    });

    const allSoOrderNotCancel = filteredOrders.filter((item) => item.operation_status !== "cancel");
    const openSoOrderNew = filteredOrders.filter(
      (item) => item.so_status === "1" && item.operation_status !== "cancel",
    );

    const sortedAllOrders = allSoOrderNotCancel.slice().sort((a, b) => a.no - b.no);
    const sortedOpenOrders = openSoOrderNew.slice().sort((a, b) => a.no - b.no);
    setAllOrder(sortedAllOrders);
    setOpenSoOrder(sortedOpenOrders);
  }, [orders, searchParams]);

  const pageOptions = ["10", "25", "50", "100"];
  const columns = [
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Order No:
          </span>
          {searchInput("searchTextOrderNo", "Search Order No", searchParams.searchTextOrderNo)}
        </div>
      ),
      dataIndex: "order_no",
      key: "order_no",
      sorter: (a: OrderSo, b: OrderSo) => {
        const A = a.order_no ?? "";
        const B = b.order_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Status:
          </span>
          {searchInput("searchTextStatus", "Search Status.", searchParams.searchTextStatus)}
        </div>
      ),
      dataIndex: "operation_status",
      key: "operation_status",
      sorter: (a: OrderSo, b: OrderSo) => {
        const A = a.operation_status ?? "";
        const B = b.operation_status ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },

    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            So No:
          </span>
          {searchInput("searchTextSo", "Search So No.", searchParams.searchTextSo)}
        </div>
      ),
      dataIndex: "so_no",
      key: "so_no",
      sorter: (a: OrderSo, b: OrderSo) => {
        const A = a.so_no ?? "";
        const B = b.so_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            So Status:
          </span>
          {searchInput("searchTextSoStatus", "Search So Status.", searchParams.searchTextSoStatus)}
        </div>
      ),
      dataIndex: "so_status",
      key: "so_status",
      sorter: (a: OrderSo, b: OrderSo) => {
        const A = a.so_status ?? "";
        const B = b.so_status ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => {
        const statusOption = statusOptionsSO.find((option) => option.value === text);
        return (
          <div style={{ textAlign: "center" }}>{statusOption ? statusOption.label : text}</div>
        );
      },
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Category:
          </span>
          {searchInput("searchTextCategory", "Search Chategory.", searchParams.searchTextCategory)}
        </div>
      ),
      dataIndex: "category",
      key: "category",
      sorter: (a: OrderSo, b: OrderSo) => {
        const A = a.category ?? "";
        const B = b.category ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Platform:
          </span>
          {searchInput("searchTextPlatform", "Search Platform", searchParams.searchTextPlatform)}
        </div>
      ),
      dataIndex: "platform",
      key: "platform",
      sorter: (a: OrderSo, b: OrderSo) => {
        const A = a.platform ?? "";
        const B = b.platform ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Store:
          </span>
          {searchInput("searchTextBrand", "Search Brand.", searchParams.searchTextBrand)}
        </div>
      ),
      dataIndex: "brand",
      key: "brand",
      sorter: (a: OrderSo, b: OrderSo) => {
        const A = a.brand ?? "";
        const B = b.brand ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Tracking No:
          </span>
          {searchInput(
            "searchTextTracking",
            "Search Tracking No.",
            searchParams.searchTextTracking,
          )}
        </div>
      ),
      dataIndex: "tracking_no",
      key: "tracking_no",
      sorter: (a: OrderSo, b: OrderSo) => {
        const A = a.tracking_no ?? "";
        const B = b.tracking_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox>
        <HeaderTitle>
          <h2>SO Management</h2>
        </HeaderTitle>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ flex: 3, marginRight: "8px" }}>
            <span>Date Range:</span>
            <DatePicker.RangePicker
              className="small-date-picker"
              style={{ marginLeft: "8px" }}
              value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
              showTime={false} // Add this line to display a single calendar
              onChange={(dates) => {
                if (dates) {
                  setStartDate(dates[0]);
                  setEndDate(dates[1]);
                } else {
                  setStartDate(null);
                  setEndDate(null);
                }
              }}
            />
          </div>
          <div style={{ flex: 2 }}>
            <Input.Search
              placeholder="Search for Order"
              value={inputData}
              onChange={(e) => setInputData(e.target.value)}
              onSearch={handleSearch}
            />
          </div>
        </div>
        <Row
          align="middle"
          justify="space-between"
          gutter={[8, 8]}
          style={{ marginBottom: "20px" }}
        >
          <Col flex={3}>
            <span>Show: </span>
            <Select
              value={pageSize.toString()}
              onChange={(value) => handlePageSizeChange(parseInt(value))}
              style={{
                marginRight: "10px", // Remove any shadow effects
              }}
            >
              {pageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            <Button
              type="primary"
              onClick={handleReloadOrder}
              icon={isReloading ? null : <ReloadOutlined />}
              loading={isReloading}
              style={{
                backgroundColor: "transparent", // Make background transparent
                borderColor: "transparent", // Remove border
                color: "#767676", // Blue color for the icon
                boxShadow: "none",
                marginRight: "10px", // Remove any shadow effects
              }}
            ></Button>
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            {activeTab === "2" ? (
              <Popconfirm
                title="Are you sure you want to open the SO?"
                onConfirm={handleOpenSo}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="primary"
                  loading={isloadingOpen}
                  // icon={isSyncing ? null : <SyncOutlined />}
                  // loading={isSyncing}
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }} // Green
                  className={`fade-in-out ${activeTab === "2" ? "visible" : ""}`}
                >
                  Open SO
                </Button>
              </Popconfirm>
            ) : null}
            {activeTab === "3" ? (
              <Button
                type="primary"
                loading={isloadingCancel}
                // onClick={handleCancelSo}
                // icon={<PlusOutlined/>}
                onClick={() => handleOpenConfirmModal(selectedRowsCancel[0])} // Assuming you want to use the first selected row
                style={{ backgroundColor: "#ff4d4f", borderColor: "#ff4d4f", color: "white" }} // Red
                className={`fade-in-out ${activeTab === "3" ? "visible" : ""}`}
              >
                Cancel SO
              </Button>
            ) : null}
          </Col>
        </Row>
        <Tabs
          defaultActiveKey="1"
          type="card"
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          onTabClick={handleTabClick}
        >
          <TabPane tab="All" key="1">
            {isloading ? (
              <Skeleton active />
            ) : (
              <Table
                columns={columns}
                dataSource={allOrders}
                scroll={{ x: 1500 }} // Adjust the value based on your needs
                pagination={{
                  position: ["bottomRight"],
                  pageSize: pageSize,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onShowSizeChange: (_, size) => handlePageSizeChange(size),
                  current: currentPage,
                  onChange: (page) => setCurrentPage(page),
                  total: allOrders.length,
                }}
              />
            )}
          </TabPane>
          <TabPane tab="Open" key="2">
            {isloading ? (
              <Skeleton active />
            ) : (
              <Table
                columns={columns}
                dataSource={openSoOrder}
                rowKey={"order_no"}
                scroll={{ x: 1500 }} // Adjust the value based on your needs
                pagination={{
                  position: ["bottomRight"],
                  pageSize: pageSize,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onShowSizeChange: (_, size) => handlePageSizeChange(size),
                  current: currentPage,
                  onChange: (page) => setCurrentPage(page),
                  total: openSoOrder.length,
                }}
                rowSelection={{
                  selectedRowKeys: selectedRowsOpen.map((row) => row.order_no), // Map OpenSo objects to their order_no values
                  onSelect: (record, selected, selectedRows) => {
                    // Update selectedRowsOpen based on row selection
                    const updatedSelectedRows = selected
                      ? [...selectedRowsOpen, record] // Add record if selected
                      : selectedRowsOpen.filter((row) => row.order_no !== record.order_no); // Remove record if deselected
                    setSelectedRowsOpen(updatedSelectedRows);
                  },
                  onSelectAll: (selected, selectedRows) => {
                    // Update selectedRowsOpen based on all rows selection
                    const updatedSelectedRows = selected ? selectedRows : [];
                    setSelectedRowsOpen(updatedSelectedRows);
                  },
                }}
              />
            )}
          </TabPane>
          <TabPane tab="Cancel" key="3">
            {isloading ? (
              <Skeleton active />
            ) : (
              <Table
                columns={columns}
                dataSource={cancelSoOrder}
                rowKey={"order_no"}
                scroll={{ x: 1500 }} // Adjust the value based on your needs
                pagination={{
                  position: ["bottomRight"],
                  pageSize: pageSize,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onShowSizeChange: (_, size) => handlePageSizeChange(size),
                  current: currentPage,
                  onChange: (page) => setCurrentPage(page),
                  total: cancelSoOrder.length,
                }}
                rowSelection={{
                  selectedRowKeys: selectedRowsCancel.map((row) => row.order_no), // Map OpenSo objects to their order_no values
                  onSelect: (record, selected, selectedRows) => {
                    // Update selectedRowsOpen based on row selection
                    const updatedSelectedRows = selected
                      ? [...selectedRowsCancel, record] // Add record if selected
                      : selectedRowsCancel.filter((row) => row.order_no !== record.order_no); // Remove record if deselected
                    setSelectedRowsCancel(updatedSelectedRows);
                  },
                  onSelectAll: (selected, selectedRows) => {
                    // Update selectedRowsCancel based on all rows selection
                    const updatedSelectedRows = selected ? selectedRows : [];
                    setSelectedRowsCancel(updatedSelectedRows);
                  },
                }}
              />
            )}
          </TabPane>
        </Tabs>
        {confirmModalInfo && confirmModalInfo.visible ? (
          <ConfirmCancelSo
            onClose={handleCloseConfirmModal}
            onMatch={handleMatch} // Pass the handleMatch function
            visible={confirmModalInfo.visible}
            trackingNo={confirmModalInfo.trackingNo}
            orderNo={confirmModalInfo.orderNo}
          />
        ) : null}
      </ContentBox>
    </LoginedLayout>
  );
};

export default SoManagement;
